import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Logger } from '@saviynt/common';
import {
  Badge,
  Box,
  ButtonIcon,
  Chip,
  Icon,
  Loader,
  Menu,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';

import useLocalization from '../../../../hooks/use-localize';
import capitalizeFirstLetter from '../../../../utils/capitalizeFirstLetter';
import normalizeStatus from '../../../../utils/integrationsDashboardUtils';
import { usePatchDiscoveryStatusMutation } from '../../../DisableCMDBDiscoveredAppsModal/data-store/changeDiscoverAppStatusSlice';
import DisableCMDBDiscoveredAppsModal from '../../../DisableCMDBDiscoveredAppsModal/DisableCMDBDiscoveredAppsModal';
import FileUploadModal from '../../../DiscoveryFileUpload/components/FileUploadModal/FileUploadModal';
import DiscoveryImport from '../../../DiscoveryImport/components/DiscoveryImport';
import {
  type IntegrationAPIItem,
  type MenuOption,
} from '../../data-layer/types';
import ApplicationOnboardingModal from '../ApplicationOnboardingModal/ApplicationOnboardingModal';

import './IntegrationsPageAllTabItems.css';

/**
 * Renders status indicator based on item status
 */
function StatusIndicator({ status }: { status: string }) {
  switch (status) {
    case 'Partially Imported':
      return (
        <Badge
          size='medium'
          color='success'
          className='IntegrationAllTab-CenterContent-Data-Item'
        />
      );
    case 'Connected':
    case 'Onboarded':
    case 'Discovered':
      return (
        <Icon
          size='small'
          kind='Check'
          color='--color-foreground-success-medium'
          className='IntegrationAllTab-CenterContent-Data-Item'
        />
      );
    case 'Disconnected':
      return (
        <Icon
          size='small'
          kind='Close'
          color='--color-foreground-critical-medium'
          className='IntegrationAllTab-CenterContent-Data-Item'
        />
      );
    case 'Active':
      return (
        <Badge
          size='medium'
          color='success'
          className='IntegrationAllTab-CenterContent-Data-Item'
        />
      );
    case 'Inactive':
      return (
        <Badge
          size='medium'
          color='error'
          className='IntegrationAllTab-CenterContent-Data-Item'
        />
      );
    default:
      return null;
  }
}

const renderLastDiscoveredOn = (timestamp?: number) => {
  const formatted =
    timestamp && !Number.isNaN(timestamp)
      ? new Date(timestamp).toLocaleDateString()
      : 'N/A';

  return <DataItem>{`Last discovered on ${formatted}`}</DataItem>;
};

/**
 * Common data item component for typography
 */
function DataItem({
  children,
  color,
}: {
  children: React.ReactNode;
  color?: string;
}) {
  return (
    <Typography
      kind='body2'
      color={color}
      className='IntegrationAllTab-CenterContent-Data-Item'>
      {children}
    </Typography>
  );
}

DataItem.defaultProps = {
  color: '--color-foreground-neutral-bold',
};

/**
 * Component to render Discovery Source content
 */
function DiscoverySourceContent({
  item,
  statusLoading,
}: {
  item: IntegrationAPIItem;
  statusLoading: boolean;
}) {
  const formatMessage = useLocalization();

  return (
    <Box className='IntegrationAllTab-CenterContent-Data'>
      {item.discoveredAppsCount != null && item.discoveredAppsCount > 0 && (
        <>
          <DataItem>{`${item.discoveredAppsCount} ${formatMessage(
            'INTEGRATIONS.DASHBOARD.APPLICATIONS_DISCOVERED',
            'Applications Discovered'
          )}`}</DataItem>
          <DataItem>•</DataItem>
        </>
      )}
      {item.owner && (
        <>
          <DataItem color='--color-foreground-neutral-subtle'>
            {formatMessage('INTEGRATIONS.DASHBOARD.OWNER_LABEL', 'Owner:')}
          </DataItem>
          <DataItem>{capitalizeFirstLetter(item.owner)}</DataItem>
          <DataItem>•</DataItem>
        </>
      )}
      {item.lastDiscoveredDate && (
        <>
          {renderLastDiscoveredOn(item.lastDiscoveredDate)}
          <DataItem>•</DataItem>
        </>
      )}
      {statusLoading ? (
        <>
          <Loader
            kind='loop'
            format='main'
            color='brand'
            size='small'
            className='IntegrationAllTab-CenterContent-Data-Item-Loader'
          />
          <DataItem>
            {formatMessage('INTEGRATIONS.ENABLING', 'Enabling')}
          </DataItem>
        </>
      ) : (
        <>
          <StatusIndicator status={normalizeStatus(item.status)} />
          <DataItem>
            {item.status === 'Disconnected'
              ? formatMessage(
                  'INTEGRATIONS.DASHBOARD.CONNECTION_FAILED',
                  'Connection Failed'
                )
              : normalizeStatus(item.status)}
          </DataItem>
        </>
      )}
    </Box>
  );
}

/**
 * Component to render Identity Source content
 */
function DiscoveredApplicationsContent({ item }: { item: IntegrationAPIItem }) {
  const formatMessage = useLocalization();

  return (
    <Box className='IntegrationAllTab-CenterContent-Data'>
      {item.owner && (
        <>
          <DataItem color='--color-foreground-neutral-subtle'>Owner:</DataItem>
          <DataItem>{item.owner}</DataItem>
          <DataItem>•</DataItem>
        </>
      )}
      {item.lastDiscoveredDate && (
        <>
          {renderLastDiscoveredOn(item.lastDiscoveredDate)}
          <DataItem>•</DataItem>
        </>
      )}
      <StatusIndicator status={normalizeStatus(item.status)} />
      <DataItem>
        {item.status === 'Disconnected'
          ? formatMessage(
              'INTEGRATIONS.DASHBOARD.CONNECTION_FAILED',
              'Connection Failed'
            )
          : normalizeStatus(item.status)}
      </DataItem>
    </Box>
  );
}

/**
 * Component to render Onboarded Application content
 */
function OnboardedApplicationsContent({ item }: { item: IntegrationAPIItem }) {
  const formatMessage = useLocalization();

  return (
    <Box className='IntegrationAllTab-CenterContent-Data'>
      {item.owner && (
        <>
          <DataItem color='--color-foreground-neutral-subtle'>Owner:</DataItem>
          <DataItem>{item.owner}</DataItem>
        </>
      )}
      {item.lastDiscoveredDate && (
        <>
          <DataItem>•</DataItem>
          <DataItem>{renderLastDiscoveredOn(item.lastDiscoveredDate)}</DataItem>
        </>
      )}
      {item.userAccessCount && (
        <DataItem>
          <DataItem>•</DataItem>
          <DataItem>{`${item.userAccessCount} ${formatMessage(
            'INTEGRATIONS.DASHBOARD.USERS_HAVE_ACCESS',
            'Users have access'
          )}`}</DataItem>
        </DataItem>
      )}
      {item.applicationType !== 'GeneralApp' && (
        <>
          <DataItem>•</DataItem>
          <StatusIndicator status={normalizeStatus(item.status)} />
          <DataItem>
            {item.status === 'Disconnected'
              ? formatMessage(
                  'INTEGRATIONS.DASHBOARD.CONNECTION_FAILED',
                  'Connection Failed'
                )
              : normalizeStatus(item.status)}
          </DataItem>
        </>
      )}
    </Box>
  );
}

/**
 * Component to render appropriate content based on item type
 */
export function ItemContent({
  item,
  statusLoading,
}: {
  item: IntegrationAPIItem;
  statusLoading: boolean;
}) {
  switch (item.type) {
    case 'DISCOVERY_SOURCE':
      return (
        <DiscoverySourceContent item={item} statusLoading={statusLoading} />
      );
    case 'DISCOVERED_APP':
      return <DiscoveredApplicationsContent item={item} />;
    case 'ONBOARDED_APP':
      return <OnboardedApplicationsContent item={item} />;
    default:
      return null;
  }
}

/**
 * Component to render trailing content with type-specific chip
 */
export function TrailingContent({
  item,
  setIntegrationList,
  setStatusLoadingIds,
  setAlertVisible,
  setAlertMessage,
}: {
  item: IntegrationAPIItem;
  setIntegrationList: React.Dispatch<
    React.SetStateAction<IntegrationAPIItem[]>
  >;
  setStatusLoadingIds: React.Dispatch<React.SetStateAction<Set<number>>>;
  setAlertVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setAlertMessage: React.Dispatch<React.SetStateAction<string>>;
}) {
  const history = useHistory();
  const formatMessage = useLocalization();

  const [isFileUploadOpen, setFileUploadOpen] = useState(false);
  const [
    isDisableDiscoveredAppsModalOpen,
    setIsDisableDiscoveredAppsModalOpen,
  ] = useState(false);
  const [isDiscoveryModelOpen, setDiscoveryModelOpen] = useState(false);
  const [isApplicationOnboardingModalOpen, setApplicationOnboardingModalopen] =
    useState(false);

  const trigger = (
    <ButtonIcon
      size='medium'
      kind='ghost'
      icon={<Icon kind='MoreVertical' color='neutral-100' size='large' />}
      dataTestId='integrations-dashboard-kebab-menu'
    />
  );

  const getInitialOptions = () => {
    const { type, applicationType, hidden } = item;

    if (type === 'DISCOVERY_SOURCE' && applicationType === 'FileUpload') {
      return [
        {
          label: formatMessage('INTEGRATIONS.DASHBOARD.REUPLOAD', 'Re-upload'),
          value: 'Re-upload',
        },
      ];
    }

    if (type === 'DISCOVERY_SOURCE' && applicationType !== 'FileUpload') {
      const options = [
        {
          label: formatMessage(
            item.status === 'Inactive'
              ? 'INTEGRATIONS.BUTTON.ENABLE'
              : 'INTEGRATIONS.BUTTON.DISABLE',
            item.status === 'Inactive' ? 'Enable' : 'Disable'
          ),
          value: item.status === 'Inactive' ? 'Enable' : 'Disable',
        },
      ];

      if (item.status === 'Connected') {
        options.push({ label: 'Import Assets', value: 'Discover' });
      }

      return options;
    }

    if (type === 'DISCOVERED_APP') {
      return [
        {
          label: formatMessage('INTEGRATIONS.DASHBOARD.ONBOARD', 'Onboard'),
          value: 'Onboard',
        },
        {
          label: formatMessage(
            hidden
              ? 'INTEGRATIONS.DASHBOARD.UNHIDE'
              : 'INTEGRATIONS.DASHBOARD.HIDE',
            hidden ? 'Unhide' : 'Hide'
          ),
          value: hidden ? 'Unhide' : 'Hide',
        },
      ];
    }

    return [];
  };

  const navigationToDiscovery = () =>
    item.appTypeId
      ? history.push(`/integrations/discovery/config?id=${item.id}`)
      : '';

  const navigationToOldAobConfigurations = () => {
    document.cookie = `discoveredAppId=${item.id}; path=/;`;
    history.push(
      `design/catalogManagement?searchString=&tab=1&name=${item.name}`
    );
  };

  const navigationToOldAobConfigurationsWithIdandType = () => {
    document.cookie = `discoveredAppId=${item.id}; path=/;`;
    history.push(`design/catalogManagement/${item.applicationType}/${item.id}`);
  };

  const navigateToCua = () => {
    history.push(`/integrations/copilot?endpointKey=${item.id}&isEdit=true`);
  };

  const navigationToOldAobConfigurationsWithSearchString = () => {
    document.cookie = `discoveredAppId=${item.id}; path=/;`;
    history.push(
      `design/catalogManagement?searchString=${
        item.applicationTypeDisplayName ?? ''
      }&tab=1&name=${item.name}`
    );
  };

  const [postStatus] = usePatchDiscoveryStatusMutation();

  const handleEnable = async () => {
    setStatusLoadingIds((prev) => new Set(prev).add(item.id));

    try {
      const resp = await postStatus({
        id: item.id,
        type: 'Discovery_Source',
        status: 'enable',
      }).unwrap();

      if (typeof resp === 'object' && 'status' in resp) {
        setIntegrationList((prevList) =>
          prevList.map((integration) =>
            integration.id === item.id
              ? { ...integration, status: normalizeStatus(resp.status) }
              : integration
          )
        );
      } else {
        Logger.warn('Unexpected enable response');
      }
    } catch (e) {
      Logger.error('Error enabling item');
      setAlertVisible(true);
      setAlertMessage('Failed to enable this integration. Please try again.');
    } finally {
      setStatusLoadingIds((prev) => {
        const copy = new Set(prev);

        copy.delete(item.id);

        return copy;
      });
    }
  };

  const handleHide = async () => {
    setIntegrationList((prevList) =>
      prevList.map((integration) =>
        integration.id === item.id
          ? { ...integration, hidden: true }
          : integration
      )
    );

    try {
      const resp = await postStatus({
        id: item.id,
        type: 'Discovery_APP',
        status: 'hide',
      }).unwrap();

      if (typeof resp !== 'object' || !('hidden' in resp)) {
        throw new Error('Invalid response format for hide');
      }
    } catch (e) {
      Logger.error('Error hiding item');
      setAlertVisible(true);
      setAlertMessage('Failed to hide this integration. Please try again.');
      setIntegrationList((prevList) =>
        prevList.map((integration) =>
          integration.id === item.id
            ? { ...integration, hidden: false }
            : integration
        )
      );
    }
  };

  const handleUnhide = async () => {
    setIntegrationList((prevList) =>
      prevList.map((integration) =>
        integration.id === item.id
          ? { ...integration, hidden: false }
          : integration
      )
    );

    try {
      const resp = await postStatus({
        id: item.id,
        type: 'Discovery_APP',
        status: 'show',
      }).unwrap();

      if (typeof resp !== 'object' || !('hidden' in resp)) {
        throw new Error('Invalid response format for unhide');
      }
    } catch (e) {
      Logger.error('Error unhiding item');
      setAlertVisible(true);
      setAlertMessage('Failed to unhide this integration. Please try again.');

      setIntegrationList((prevList) =>
        prevList.map((integration) =>
          integration.id === item.id
            ? { ...integration, hidden: true }
            : integration
        )
      );
    }
  };

  const handleChange = (selectedOption: MenuOption) => {
    switch (selectedOption.value) {
      case 'Re-upload':
        setFileUploadOpen(true);
        break;
      case 'Discover':
        setDiscoveryModelOpen(true);
        break;
      case 'Onboard':
        if (!item.applicationTypeDisplayName) {
          navigationToOldAobConfigurations();
        } else {
          setApplicationOnboardingModalopen(true);
        }

        break;
      case 'Disable':
        setIsDisableDiscoveredAppsModalOpen(true);
        break;
      case 'Enable':
        // eslint-disable-next-line no-void
        void handleEnable();
        break;
      case 'Hide':
        // eslint-disable-next-line no-void
        void handleHide();
        break;
      case 'Unhide':
        // eslint-disable-next-line no-void
        void handleUnhide();
        break;
    }
  };

  const getTypeChip = () => {
    const initialOptions = getInitialOptions();

    switch (item.type) {
      case 'DISCOVERY_SOURCE':
        return (
          <>
            <Chip
              label={formatMessage(
                'INTEGRATIONS.DASHBOARD.CHIP_DISCOVERY_SOURCE',
                'Discovery Source'
              )}
              kind='subtleFilled'
              leftIcon={<Icon kind='ViewExistingAccess' />}
              className='IntegrationAllTab-TrailingContent-Components'
            />
            <ButtonIcon
              size='large'
              kind='outlinedSecondary'
              icon={<Icon kind='ArrowRight' />}
              ariaLabel='Navigate'
              onClick={() => navigationToDiscovery()}
              className={`IntegrationAllTab-TrailingContent-Components${
                item.applicationType === 'FileUpload'
                  ? '-ArrowButton-Invisible'
                  : ''
              }`}
              dataTestId='integrations-dashboard-discovery-source-arrow'
            />
            {initialOptions.length > 0 && (
              <Menu
                options={initialOptions}
                onChange={handleChange}
                trigger={trigger}
                className='IntegrationAllTab-TrailingContent-Components-KebabMenu'
                placement='bottom-end'
                isPortalEnabled
                dataTestId='integrations-dashboard-discovery-source-kebebmenu'
              />
            )}
          </>
        );

      case 'DISCOVERED_APP':
        return (
          initialOptions.length > 0 && (
            <Menu
              options={initialOptions}
              onChange={handleChange}
              trigger={trigger}
              className='IntegrationAllTab-TrailingContent-Components-KebabMenu'
              placement='bottom-end'
              isPortalEnabled
              dataTestId='integrations-dashboard-discivered-apps-kebebmenu'
            />
          )
        );

      case 'ONBOARDED_APP':
        return (
          <>
            <ButtonIcon
              size='large'
              kind='outlinedSecondary'
              icon={<Icon kind='ArrowRight' />}
              ariaLabel='Navigate'
              onClick={() => {
                if (item.applicationType === 'CUA') {
                  navigateToCua();
                } else {
                  navigationToOldAobConfigurationsWithIdandType();
                }
              }}
              className='IntegrationAllTab-TrailingContent-Components'
              dataTestId='integrations-dashboard-onboarded-apps-kebebmenu'
            />
            <Menu
              options={initialOptions}
              onChange={handleChange}
              trigger={trigger}
              className='IntegrationAllTab-TrailingContent-Components-KebabMenu-Invisible'
              placement='bottom-end'
              isPortalEnabled
              dataTestId='integrations-dashboard-discivered-apps-kebebmenu'
            />
          </>
        );

      default:
        return null;
    }
  };

  const toggleStatusBetweenActiveInactive = (resp: {
    data?: unknown;
    error?: unknown;
  }) => {
    if (
      typeof resp === 'object' &&
      'data' in resp &&
      resp.data !== null &&
      typeof resp.data === 'object' &&
      'status' in resp.data &&
      typeof (resp.data as { status: unknown }).status === 'string'
    ) {
      const newStatus = (resp.data as { status: string }).status;

      setIntegrationList((prevList) =>
        prevList.map((integration) =>
          integration.id === item.id
            ? { ...integration, status: normalizeStatus(newStatus) }
            : integration
        )
      );
    } else {
      Logger.warn('Invalid response from Disable modal', resp);
      setAlertVisible(true);
      setAlertMessage(
        'Failed to Disable the Discovery Source. Please try again.'
      );
    }

    setIsDisableDiscoveredAppsModalOpen(false);
  };

  const renderDiscoveryImportModal = () => (
    <ModalIsland
      kind='default'
      isOpen={isDiscoveryModelOpen}
      onClose={() => setDiscoveryModelOpen(false)}
      title={formatMessage('INTEGRATIONS.MODAL_TITLE.DISCOVERY', 'Discovery')}
      headerIcon={<Icon kind='ManageOverview' size='large' />}
      contentBody={
        <DiscoveryImport
          appTypeKey={item.appTypeId}
          iconLink={item.iconLink ?? ''}
          iobAppKey={item.id}
          closeStartImportModal={() => setDiscoveryModelOpen(false)}
        />
      }
    />
  );

  return (
    <>
      <Box className='IntegrationAllTab-TrailingContent'>{getTypeChip()}</Box>
      {isFileUploadOpen && (
        <FileUploadModal
          name={item.appName ?? item.name}
          isModalOpen={isFileUploadOpen}
          fileUploadSourceAppId={item.appTypeId}
          fileUploadIobAppId={item.id}
          handleCloseModal={() => setFileUploadOpen(false)}
        />
      )}

      {isDiscoveryModelOpen && renderDiscoveryImportModal()}

      {isDisableDiscoveredAppsModalOpen && (
        <DisableCMDBDiscoveredAppsModal
          isOpen={isDisableDiscoveredAppsModalOpen}
          setIsOpen={setIsDisableDiscoveredAppsModalOpen}
          type='DISCOVERY_SOURCE'
          id={item.id}
          name={item.name}
          status='disable'
          changeStatusCallback={toggleStatusBetweenActiveInactive}
        />
      )}

      {isApplicationOnboardingModalOpen ? (
        <ApplicationOnboardingModal
          isOpen={isApplicationOnboardingModalOpen}
          applicationName={item.applicationTypeDisplayName ?? item.name}
          applicationDescription={
            item.applicationTypeDescription ??
            'Are you sure you want to onboard this application?'
          }
          applicationIcon={item.iconLink ?? ''}
          handleClose={() => setApplicationOnboardingModalopen(false)}
          handleSelectDifferentApplication={() => {
            navigationToOldAobConfigurations();
          }}
          handleConfirmApplicationOnboarding={() => {
            navigationToOldAobConfigurationsWithSearchString();
          }}
        />
      ) : (
        ''
      )}
    </>
  );
}
